import { Injectable } from '@angular/core';
import { environment } from '../../../../../environments/environment.development';
import {
  CaseMemberListResponse,
  CaseMemberQueryParams,
  CaseOrganizationResponse,
  CreateCaseMemberPayload,
  CreateCaseMemberResponse,
  DeleteCaseMemberResponse,
  UpdateCaseMemberPayload,
  UpdateCaseMemberResponse,
  UserApiResponsePaginated,
} from '../../insights.interface';
import { BehaviorSubject, catchError, Observable, tap } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ErrorHandlingService } from '../../../../core/services/error-handling.service';

@Injectable({
  providedIn: 'root',
})
export class CaseMemberService {
  private readonly API_URL = `${environment.apiUrl}`;

  private caseMemberSubject =
    new BehaviorSubject<CaseMemberListResponse | null>(null);
  private caseOrgSubject = new BehaviorSubject<CaseOrganizationResponse | null>(
    null
  );
  private usersSubject = new BehaviorSubject<UserApiResponsePaginated | null>(
    null
  );

  readonly caseMemberList$ = this.caseMemberSubject.asObservable();
  readonly caseOrganizations$ = this.caseOrgSubject.asObservable();
  readonly users$ = this.usersSubject.asObservable();

  constructor(
    private errorHandler: ErrorHandlingService,
    private http: HttpClient
  ) {}

  private buildQueryParams(
    params: Record<string, string | number>
  ): HttpParams {
    return Object.entries(params).reduce(
      (httpParams, [key, value]) => httpParams.set(key, value.toString()),
      new HttpParams()
    );
  }

  getCaseMemberList(
    queryParams: CaseMemberQueryParams
  ): Observable<CaseMemberListResponse> {
    const params = this.buildQueryParams({
      PageSize: queryParams.pageSize,
      Page: queryParams.pageIndex,
      CaseId: queryParams.caseId,
      CaseMemberName: queryParams.searchTerm,
      Sort: queryParams.sortField,
      sortOrder: queryParams.sortOrder ?? '0',
    });

    return this.http
      .get<CaseMemberListResponse>(`${this.API_URL}/CaseMember/list`, {
        params,
      })
      .pipe(
        tap(response => this.caseMemberSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getCaseOrganizations(caseId: number): Observable<CaseOrganizationResponse> {
    const params = this.buildQueryParams({ caseId });

    return this.http
      .get<CaseOrganizationResponse>(
        `${this.API_URL}/CaseMember/org-parent-child-list`,
        { params }
      )
      .pipe(
        tap(response => this.caseOrgSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  createCaseMember(
    payload: CreateCaseMemberPayload
  ): Observable<CreateCaseMemberResponse> {
    return this.http
      .post<CreateCaseMemberResponse>(
        `${this.API_URL}/CaseMember/create`,
        payload
      )
      .pipe(
        tap(() => this.refreshCaseMemberList(payload.caseId)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  updateCaseMember(
    caseId: number,
    caseMemberId: number,
    payload: UpdateCaseMemberPayload
  ): Observable<UpdateCaseMemberResponse> {
    const params = this.buildQueryParams({
      caseId,
      caseMemberId,
    });

    return this.http
      .put<UpdateCaseMemberResponse>(
        `${this.API_URL}/CaseMember/update`,
        payload,
        { params }
      )
      .pipe(
        tap(() => this.refreshCaseMemberList(caseId)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  deleteCaseMember(
    memberId: number,
    caseId: number
  ): Observable<DeleteCaseMemberResponse> {
    return this.http
      .delete<DeleteCaseMemberResponse>(
        `${this.API_URL}/CaseMember/${memberId}`
      )
      .pipe(
        tap(() => this.refreshCaseMemberList(caseId)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  addToDashboard(caseIds: number[]): Observable<void> {
    const url = `${this.API_URL}/CaseMember/add-to-dashboard`;
    return this.http
      .post<void>(url, caseIds)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
  getAllUsers(params: {
    organizationId?: number;
    pageSize: number;
    page: number;
    searchTerm: string;
    Sort: string;
    sortOrder: number;
  }): Observable<UserApiResponsePaginated> {
    let httpParams = new HttpParams()
      .set('pageSize', params.pageSize.toString())
      .set('page', params.page.toString())
      .set('searchTerm', params.searchTerm)
      .set('Sort', params.Sort)
      .set('sortOrder', params.sortOrder.toString());

    if (params.organizationId !== undefined) {
      httpParams = httpParams.set(
        'organizationId',
        params.organizationId.toString()
      );
    }

    return this.http
      .get<UserApiResponsePaginated>(`${this.API_URL}/User`, {
        params: httpParams,
      })
      .pipe(
        tap(response => this.usersSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  clearUsers(): void {
    this.usersSubject.next(null);
  }

  private refreshCaseMemberList(caseId: number): void {
    this.getCaseMemberList({
      searchTerm: '',
      sortField: '',
      pageIndex: 1,
      pageSize: 10,
      caseId,
    }).subscribe();
  }
}
